import { Music2 } from 'lucide-react'
import { TAG_META, fitFor, fitLabel, type BuildItem, type Tag } from '@/components/build/types'

export function TagChip({ tag, className = '' }: { tag: Tag; className?: string }) {
  const meta = TAG_META[tag]
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium ${className}`}
      style={{ backgroundColor: `color-mix(in oklab, ${meta.color} 14%, transparent)`, color: `color-mix(in oklab, ${meta.color} 78%, var(--foreground))` }}
    >
      <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: meta.color }} />
      <span className="truncate">{meta.label}</span>
    </span>
  )
}

// A song's cover, or -- for a track with no art (every essential) -- a tile
// tinted with its kind's colour.
export function Cover({ item, className = '' }: { item: BuildItem; className?: string }) {
  const color = TAG_META[item.tag].color
  return item.image_url ? (
    <img src={item.image_url} alt="" loading="lazy" className={`size-full object-cover ${className}`} />
  ) : (
    <div
      className={`flex size-full items-center justify-center ${className}`}
      style={{ background: `linear-gradient(135deg, color-mix(in oklab, ${color} 35%, transparent), color-mix(in oklab, ${color} 10%, transparent))`, color }}
    >
      <Music2 className="size-1/3" />
    </div>
  )
}

// Where this song usually sits in a ride (ring) against where it's been put
// (dot). Drag a warm-up song to the end and the dot slides away from the ring.
export function PositionMarker({ item, index, count }: { item: BuildItem; index: number; count: number }) {
  const fit = fitFor(item, index, count)
  if (fit == null || item.avg_position == null) return null
  const label = fitLabel(fit)
  const here = count <= 1 ? 0.5 : index / (count - 1)

  return (
    <div className="flex items-center gap-2 text-xs" title={`Across past rides this song usually sits about ${Math.round(item.avg_position * 100)}% of the way through`}>
      <div className="relative h-1.5 w-16 shrink-0 rounded-full bg-muted">
        <span className="absolute -top-[3px] size-3 -translate-x-1/2 rounded-full border-2 border-foreground/40 bg-background" style={{ left: `${item.avg_position * 100}%` }} />
        <span className="absolute -top-[3px] size-3 -translate-x-1/2 rounded-full bg-primary transition-[left] duration-500" style={{ left: `${here * 100}%` }} />
      </div>
      <span className={label.good ? 'text-muted-foreground' : 'text-amber-600 dark:text-amber-400'}>{label.text}</span>
    </div>
  )
}

// Three bars that dance while this row's preview is playing.
export function Equalizer() {
  return (
    <span aria-hidden className="flex h-4 items-end gap-[2px]">
      {[0, 1, 2].map((i) => (
        <span key={i} className="eq-bar w-[3px] rounded-full bg-primary" style={{ animationDelay: `${i * 0.18}s` }} />
      ))}
    </span>
  )
}
